import { Plus } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { DataTable, Pagination, type Column, type RowKey } from "@/components/ui/data-table"
import { Dialog, DialogTrigger } from "@/components/ui/dialog"
import { EmptyState } from "@/components/ui/empty-state"
import { Kbd } from "@/components/ui/stat-card"
import { ActiveFilterChips } from "@/components/ui/table-filters"
import { type SortOption } from "@/components/ui/table-sort"
import { TableToolbar } from "@/components/ui/table-toolbar"
import { useToast } from "@/components/ui/toast"
import { COMING_SOON, FEATURES } from "@/lib/features"
import { useTable } from "@/lib/table"
import { useSlashShortcut } from "@/lib/use-slash-shortcut"

import { bannerFor } from "@/lib/api-error-message"

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { ClientCell, MembersCell, ReviewsCell, SinceCell } from "./clients-cells"
import { NewEngagementDialog } from "./clients-dialogs"
import {
  clientUsersQuery,
  clientsQuery,
} from "./clients-api"
import {
  filterClients,
  type ClientRow,
  type ClientUserOption,
  type UserId,
} from "./clients-data"
import { buildClientsFilterSchema, filtersToQuery } from "./clients-filters"
import { ExpandedMembers } from "./clients-members"
import { useClientsStore } from "./clients-store"

const EMPTY_ROWS: ClientRow[] = []
const EMPTY_USERS: ClientUserOption[] = []

const SORT_OPTIONS: SortOption[] = [
  { id: "createdAt", label: "Since", ascLabel: "Oldest first", descLabel: "Newest first" },
  { id: "name", label: "Client name", ascLabel: "A → Z", descLabel: "Z → A" },
  { id: "reviewCount", label: "Reviews", ascLabel: "Fewest first", descLabel: "Most first" },
  { id: "members", label: "Members", ascLabel: "Fewest first", descLabel: "Most first" },
]

export function ClientsPage(): JSX.Element {
  const schema = React.useMemo(() => buildClientsFilterSchema(), [])
  const { toast } = useToast()
  const searchRef = React.useRef<HTMLInputElement>(null)
  const [expanded, setExpanded] = React.useState<Set<RowKey>>(new Set())
  const [createOpen, setCreateOpen] = React.useState(false)

  // GET /clients + /users are both admin-only server-side. The users roster is
  // fetched once (small, static-ish); the engagement page is server-paginated.
  const { data: seedUsers = EMPTY_USERS, error: loadError } = useQuery(clientUsersQuery())

  const store = useClientsStore(EMPTY_ROWS, seedUsers)

  useSlashShortcut(searchRef)

  const userName = React.useCallback(
    (userId: UserId) => seedUsers.find((u) => u.id === userId)?.name ?? "Contact",
    [seedUsers],
  )

  const memberActions = React.useCallback(
    (row: ClientRow) => ({
      onAddMember: (userId: UserId) => {
        store.addMember(row.id, userId)
        toast({
          tone: "success",
          title: `${userName(userId)} added to ${row.name}`,
          action: { label: "Undo", onAction: () => store.removeMember(row.id, userId) },
        })
      },
      onRemoveMember: (userId: UserId) => {
        store.removeMember(row.id, userId)
        toast({
          tone: "success",
          title: `${userName(userId)} removed from ${row.name}`,
          action: { label: "Undo", onAction: () => store.addMember(row.id, userId) },
        })
      },
      onMakePrimary: (userId: UserId) => {
        const previous = row.primaryContactId
        store.makePrimary(row.id, userId)
        toast({
          tone: "success",
          title: `${userName(userId)} is now the primary contact`,
          action: previous
            ? { label: "Undo", onAction: () => store.makePrimary(row.id, previous) }
            : undefined,
        })
      },
    }),
    [store, toast, userName],
  )

  const qc = useQueryClient()
  const fetchData = React.useCallback(
    async (params: {
      page: number
      pageSize: number
      sort: { field: string; dir: "asc" | "desc" } | null
      filters: Record<string, unknown>
      search: string
    }) => {
      const page = await qc.fetchQuery(
        clientsQuery(qc, {
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          sort: params.sort,
        }),
      )
      return { rows: page.items, total: page.total }
    },
    [qc],
  )

  const { state, actions } = useTable<ClientRow>({
    fetchData,
    defaultSort: { field: "createdAt", dir: "desc" },
    storageKey: "clients",
  })

  // Local axes (status, hasPrimary, createdAt) still apply within the current page.
  // Server-side wiring for these is task #26/#38 territory.
  const filteredRows = React.useMemo(
    () => filterClients(state.rows, filtersToQuery(state.filters as Parameters<typeof filtersToQuery>[0])),
    [state.rows, state.filters],
  )

  const toggleExpand = React.useCallback((key: RowKey) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const columns = React.useMemo<Array<Column<ClientRow>>>(
    () => [
      {
        id: "client",
        header: "Client",
        width: "minmax(0,1.4fr)",
        cell: (r) => <ClientCell row={r} />,
      },
      {
        id: "members",
        header: "Who can see it",
        width: "168px",
        priority: 2,
        cell: (r) => <MembersCell row={r} />,
      },
      {
        id: "reviews",
        header: "Reviews",
        width: "168px",
        priority: 2,
        cell: (r) => <ReviewsCell row={r} />,
      },
      {
        id: "since",
        header: "Since",
        width: "108px",
        align: "end",
        priority: 2,
        cell: (r) => <SinceCell row={r} />,
      },
    ],
    [],
  )

  const filtered = state.search.length > 0 || state.filterCount > 0

  return (
    <div className="flex h-full flex-col gap-5 px-6 py-5">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-subtitle text-text">Clients</h1>
        {!FEATURES.newEngagement && (
          <Button
            variant="primary"
            leadingIcon={<Plus />}
            onClick={() => {
              toast(COMING_SOON)
            }}
          >
            New engagement
          </Button>
        )}
        {FEATURES.newEngagement && (
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button variant="primary" leadingIcon={<Plus />}>
                New engagement
              </Button>
            </DialogTrigger>
          <NewEngagementDialog
            users={seedUsers}
            onSubmit={(draft) => {
              store.create(draft)
              setCreateOpen(false)
              toast({ tone: "success", title: `${draft.name} added` })
            }}
          />
        </Dialog>
        )}
      </header>

      {loadError && (() => {
        const banner = bannerFor(loadError, "engagements")
        return (
          <div
            role="alert"
            className="rounded-md border border-people/30 bg-people-tint/40 px-4 py-3 text-body-sm text-people"
          >
            <div className="font-medium">{banner.title}</div>
            <div className="mt-0.5">{banner.body}</div>
          </div>
        )
      })()}

      <TableToolbar
        search={state.search}
        onSearchChange={actions.setSearch}
        searchRef={searchRef}
        searchPlaceholder="Filter by client, contact name or email"
        searchAriaLabel="Filter engagements"
        searchSuffix={state.search.length === 0 ? <Kbd keys="/" /> : null}
        filterSchema={schema}
        filterValues={state.filters}
        onFiltersChange={actions.setFilters}
        onClearAllFilters={actions.clearAllFilters}
        sortOptions={SORT_OPTIONS}
        sort={state.sort}
        onSortChange={actions.setSort}
      />

      <ActiveFilterChips
        schema={schema}
        values={state.filters}
        onRemove={actions.clearFilter}
        onClearAll={actions.clearAllFilters}
      />

      <div className="min-h-0 flex-1">
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<ClientRow>
            label="Engagements"
            columns={columns}
            rows={filteredRows}
            rowKey={(r) => r.id}
            expandedKeys={expanded}
            onToggleExpand={toggleExpand}
            renderExpanded={(row) => (
              <ExpandedMembers
                row={row}
                users={seedUsers}
                actions={memberActions(row)}
              />
            )}
            stickyHeader
            loading={state.isLoading}
            className="rounded-none border-0"
            empty={
              <EmptyState
                bare
                reason={filtered ? "filtered" : "empty"}
                title={filtered ? "Nothing matches" : "No engagements yet"}
                body={
                  filtered
                    ? "Try clearing the search or filters. Reviews can still run — they just are not visible to any client login."
                    : "Create one to give a client login access to its own reviews. Reviews can still run without one — they just are not visible to any client login."
                }
                action={
                  filtered ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        actions.setSearch("")
                        actions.clearAllFilters()
                      }}
                    >
                      Clear filters
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      leadingIcon={<Plus />}
                      onClick={() => {
                        if (FEATURES.newEngagement) setCreateOpen(true)
                        else toast(COMING_SOON)
                      }}
                    >
                      New engagement
                    </Button>
                  )
                }
              />
            }
          />
          </div>
          <div className="border-t border-border">
            <Pagination
              page={state.page}
              pageSize={state.pageSize}
              total={state.total}
              onPageChange={actions.setPage}
              onPageSizeChange={actions.setPageSize}
              pageSizes={[10, 25, 50]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
